import React, { useState } from 'react';

interface MailLogEntry {
  id: number;
  timestamp: string | null;
  log_reason: string | null;
  message: string | null;
  mail_action: string | null;
}

const MailLogScreen: React.FC<{ apiBaseUrl: string; accessToken: string | null }> = ({ apiBaseUrl, accessToken }) => {
  const [entries, setEntries] = useState<MailLogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [queried, setQueried] = useState(false);
  const [queryError, setQueryError] = useState<string | null>(null);
  const [actionFilter, setActionFilter] = useState<string>('all');

  const fetchMailLog = async (overrideAction?: string) => {
    const filter = overrideAction ?? actionFilter;
    setLoading(true);
    try {
      let url = `${apiBaseUrl}/email/mail-log`;
      if (filter !== 'all') {
        url += `?action=${encodeURIComponent(filter)}`;
      }
      const res = await fetch(url, {
        headers: accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {},
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        setEntries([]);
        setQueryError(errData.message || `Request failed (${res.status})`);
        setQueried(true);
        return;
      }
      const data = await res.json();
      setEntries(Array.isArray(data) ? data : []);
      setQueryError(null);
      setQueried(true);
    } catch (err) {
      setEntries([]);
      setQueryError('Network error');
      setQueried(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-bold mb-4">Mail Log</h2>
      <p className="text-gray-600 mb-4">Records of email sending activity.</p>

      {/* Filter controls */}
      <div className="flex items-center gap-3 mb-4">
        <label htmlFor="actionFilter" className="text-sm font-medium text-gray-700">
          Filter:
        </label>
        <select
          id="actionFilter"
          className="border rounded px-3 py-2 text-sm"
          value={actionFilter}
          onChange={(e) => {
            const selected = e.target.value;
            setActionFilter(selected);
            setLoading(true);
            fetchMailLog(selected);
          }}
          disabled={loading}
        >
          <option value="all">All</option>
          <option value="sent">Sent</option>
          <option value="failed">Failed</option>
        </select>
        <button
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-800 text-sm"
          onClick={() => fetchMailLog(actionFilter)}
          disabled={loading}
        >
          {loading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      {queried && !loading && queryError && (
        <div className="text-red-500 mb-4">{queryError}</div>
      )}
      {queried && !loading && entries.length === 0 && (
        <div className="text-gray-500">No mail log records found.</div>
      )}
      {loading ? <div>Loading...</div> : entries.length > 0 && (
        <table className="min-w-full border text-xs">
          <thead>
            <tr>
              <th className="py-2 px-3 bg-gray-100">ID</th>
              <th className="py-2 px-3 bg-gray-100">Timestamp</th>
              <th className="py-2 px-3 bg-gray-100">Action</th>
              <th className="py-2 px-3 bg-gray-100">Reason</th>
              <th className="py-2 px-3 bg-gray-100">Message</th>
            </tr>
          </thead>
          <tbody>
            {entries.map(e => (
              <tr key={e.id} className="hover:bg-gray-50 border-b">
                <td className="py-2 px-3">{e.id}</td>
                <td className="py-2 px-3">{e.timestamp ? new Date(e.timestamp).toLocaleString() : '—'}</td>
                <td className="py-2 px-3">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                    e.mail_action === 'sent'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {e.mail_action || '—'}
                  </span>
                </td>
                <td className="py-2 px-3">{e.log_reason}</td>
                <td className="py-2 px-3 text-gray-700">{e.message}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default MailLogScreen;
